import {
  DestroyRef,
  Signal,
  computed,
  effect,
  inject,
  signal,
  untracked,
  Service,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DashboardHttpService } from '../services/dashboard-http.service';
import type {
  TimeOverrideConfig,
  WidgetDataResponse,
} from '../types/dashboard.types';
import { DashboardEditSessionStore } from './dashboard-edit-session.store';
import type { PlacedWidget } from './grid-layout/grid-layout.component';

export interface EditWidgetDataState {
  data: WidgetDataResponse | null;
  loading: boolean;
  error: boolean;
}

interface WidgetDataKeyEntry {
  localId: string;
  key: string;
}

interface SurfaceContext {
  boundId: string | null;
  loadStatus: string;
}

function canonicalise(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(canonicalise);
  }
  if (value !== null && typeof value === 'object') {
    const source = value as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(source).sort()) {
      const entry = source[key];
      if (entry === null || entry === undefined) continue;
      result[key] = canonicalise(entry);
    }
    return result;
  }
  return value;
}

/** Fetch key only, and the request body sent to `previewWidget` stays raw. */
export function widgetDataKey(
  widget: PlacedWidget,
  timeContext: TimeOverrideConfig
): string {
  return JSON.stringify(
    canonicalise({
      widgetType: widget.widgetType,
      queryConfig: widget.queryConfig,
      timeOverride: widget.timeOverride ?? timeContext,
    })
  );
}

function equalKeyEntryLists(
  a: WidgetDataKeyEntry[],
  b: WidgetDataKeyEntry[]
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].localId !== b[i].localId || a[i].key !== b[i].key) return false;
  }
  return true;
}

@Service({ autoProvided: false })
export class DashboardEditWidgetDataService {
  private readonly store = inject(DashboardEditSessionStore);
  private readonly dashboardHttpService = inject(DashboardHttpService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly statesSignal = signal<Record<string, EditWidgetDataState>>(
    {}
  );

  private retainedKeys = new Map<string, string>();
  private surfaceGeneration = 0;
  private widgetGenerations: Record<string, number> = {};
  private previousSurfaceContext: SurfaceContext | null = null;

  readonly states: Signal<Record<string, EditWidgetDataState>> =
    this.statesSignal.asReadonly();
  readonly timeContext: Signal<TimeOverrideConfig> = this.store.timeContext;

  private readonly dataKeys = computed(
    () =>
      this.store.placedWidgets().map(widget => ({
        localId: widget.localId,
        key: widgetDataKey(widget, this.store.timeContext()),
      })),
    { equal: equalKeyEntryLists }
  );

  constructor() {
    effect(() => {
      // Recorded on every run, including early returns, so a same-id
      // loaded→loading→loaded reseed still sees the prior 'loading' status.
      const loadStatus = this.store.loadStatus();
      const boundId = this.store.previewDashboardId();

      const previous = this.previousSurfaceContext;
      this.previousSurfaceContext = { boundId, loadStatus };

      if (loadStatus !== 'loaded') return;

      const keys = this.dataKeys();

      const surfaceChanged =
        !previous ||
        previous.boundId !== boundId ||
        previous.loadStatus !== loadStatus;
      if (surfaceChanged) {
        this.surfaceGeneration++;
        this.retainedKeys = new Map();
        this.statesSignal.set({});
        this.widgetGenerations = {};
      }

      const widgets = untracked(() => this.store.placedWidgets());
      const widgetsById = new Map(
        widgets.map(widget => [widget.localId, widget])
      );
      const currentIds = new Set(keys.map(entry => entry.localId));

      for (const id of [...this.retainedKeys.keys()]) {
        if (currentIds.has(id)) continue;
        this.retainedKeys.delete(id);
        delete this.widgetGenerations[id];
        this.statesSignal.update(current => {
          if (!(id in current)) return current;
          const next = { ...current };
          delete next[id];
          return next;
        });
      }

      const surfaceGeneration = this.surfaceGeneration;

      for (const { localId, key } of keys) {
        if (this.retainedKeys.get(localId) === key) continue;
        this.retainedKeys.set(localId, key);

        const widget = widgetsById.get(localId);
        if (!widget) continue;

        this.statesSignal.update(current => ({
          ...current,
          [localId]: {
            data: null,
            loading: true,
            error: false,
          },
        }));

        const widgetGeneration = (this.widgetGenerations[localId] ?? 0) + 1;
        this.widgetGenerations[localId] = widgetGeneration;

        if (!boundId) continue;

        this.dashboardHttpService
          .previewWidget(boundId, {
            widgetType: widget.widgetType,
            queryConfig: widget.queryConfig,
            timeOverride: widget.timeOverride ?? this.store.timeContext(),
          })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: response => {
              if (
                !this.isCurrentResponse(
                  surfaceGeneration,
                  localId,
                  widgetGeneration
                )
              )
                return;
              this.setState(localId, {
                data: response?.data ?? null,
                loading: false,
                error: false,
              });
            },
            error: () => {
              if (
                !this.isCurrentResponse(
                  surfaceGeneration,
                  localId,
                  widgetGeneration
                )
              )
                return;
              this.setState(localId, {
                data: null,
                loading: false,
                error: true,
              });
            },
          });
      }
    });
  }

  setTimeContext(config: TimeOverrideConfig): void {
    this.store.setTimeContext(config);
  }

  private isCurrentResponse(
    surfaceGeneration: number,
    localId: string,
    widgetGeneration: number
  ): boolean {
    return (
      surfaceGeneration === this.surfaceGeneration &&
      widgetGeneration === this.widgetGenerations[localId]
    );
  }

  private setState(localId: string, state: EditWidgetDataState): void {
    this.statesSignal.update(current => ({ ...current, [localId]: state }));
  }
}
